import React from 'react';
import './index.scss';

const Privacy = () => {
  return (
    <div className='legal-page'>
      <div className='legal-container'>
        <div className='legal-header'>
          <h1>Privacy Policy</h1>
          <p className='legal-meta'>Last updated: May 14, 2026</p>
        </div>

        <p>
          This Privacy Policy describes how FM Home Loans, LLC (also doing
          business as MR Capital Group), a New York–licensed residential
          mortgage lender (NMLS ID: 2212) ("FM Home Loans," "we," "us"), handles
          personal information about FM Home Loans personnel in connection with
          CinchLo, our internal pipeline management application.
        </p>
        <p>
          <strong>This policy applies to CinchLo only.</strong> CinchLo is an
          internal application used exclusively by authorized FM Home Loans
          personnel. For information about how FM Home Loans handles consumer
          and borrower personal information in the course of mortgage
          origination, servicing, and related business activities, see our
          general Privacy Policy at{' '}
          <a
            href='https://www.fmm.com/privacy'
            target='_blank'
            rel='noopener noreferrer'
          >
            www.fmm.com/privacy
          </a>
          .
        </p>

        <div className='legal-section'>
          <h2>Information We Collect</h2>

          <h3>Identification & access</h3>
          <p>
            When you authenticate to CinchLo, we receive and process the
            following from Microsoft Entra ID single sign-on:
          </p>
          <ul>
            <li>Your Entra Object ID (an immutable user identifier)</li>
            <li>Your corporate email address and display name</li>
            <li>
              Your Encompass User ID (provided via your Entra profile extension
              attribute)
            </li>
          </ul>

          <h3>Application activity</h3>
          <ul>
            <li>
              Authentication events (sign-in timestamps, source IP addresses,
              user-agent strings)
            </li>
            <li>Pages and loan records accessed within CinchLo</li>
            <li>Administrative actions taken within CinchLo</li>
            <li>Your notification preferences and saved views</li>
          </ul>

          <h3>Notification channel information (opt-in)</h3>
          <p>
            If you enable SMS, email, push, or in-app notifications, CinchLo
            collects and stores:
          </p>
          <ul>
            <li>Mobile phone number (only if you enable SMS notifications)</li>
            <li>
              Web Push subscription endpoints (only if you enable push
              notifications in your browser or installed app)
            </li>
            <li>
              Your per-event-type notification preferences and quiet-hours
              settings
            </li>
            <li>
              An audit record of your opt-in, including timestamp, IP address,
              and the consent text shown at the time you opted in
            </li>
          </ul>

          <h3>Loan and borrower information displayed in CinchLo</h3>
          <p>
            CinchLo displays loan, borrower, and property information sourced
            from FM Home Loans' loan origination system (Encompass). This
            information is the property of FM Home Loans and is governed by FM
            Home Loans' broader information-handling practices and all
            applicable laws and regulations, including the Gramm-Leach-Bliley
            Act, the Fair Credit Reporting Act, and 23 NYCRR Part 500. CinchLo
            does not display borrower information to any user beyond the access
            scope established by that user's Encompass roles and access groups.
          </p>
        </div>

        <div className='legal-section'>
          <h2>How We Use This Information</h2>
          <ul>
            <li>
              To authenticate users and authorize access scoped to each user's
              Encompass roles and access groups
            </li>
            <li>
              To present each user's role-appropriate loan pipeline and related
              operational information
            </li>
            <li>
              To deliver notifications via the channels each user has explicitly
              opted into
            </li>
            <li>
              To maintain audit logs required for security, compliance,
              operational, and regulatory purposes
            </li>
            <li>
              To detect, investigate, and respond to suspected unauthorized
              access or security incidents
            </li>
          </ul>
        </div>

        <div className='legal-section'>
          <h2>How We Share Information</h2>
          <p>
            CinchLo operates entirely within FM Home Loans' Microsoft Azure
            tenant. We engage the following sub-processors to operate CinchLo:
          </p>
          <ul>
            <li>
              Microsoft Azure — application hosting, database, identity (Entra
              ID), and supporting infrastructure
            </li>
            <li>Microsoft Graph API — email notification delivery</li>
            <li>Azure Communication Services — SMS notification delivery</li>
            <li>
              ICE Mortgage Technology (Encompass) — loan-system source of record
            </li>
          </ul>
          <p>
            We do not sell, rent, or otherwise share personal information
            collected through CinchLo — including mobile phone numbers — with
            third parties for marketing purposes. We may disclose information
            when required by law, in response to lawful process, or to protect
            the rights, property, or safety of FM Home Loans, its personnel, or
            its customers.
          </p>
        </div>

        <div className='legal-section'>
          <h2>SMS-Specific Practices</h2>
          <p>
            CinchLo's SMS notification feature is operated using toll-free
            number 1-877-740-4425. Mobile phone numbers collected through
            CinchLo are used solely to deliver CinchLo transactional
            notifications to the employee who provided the number.
          </p>
          <p>
            You may opt out of all CinchLo SMS notifications at any time by:
          </p>
          <ul>
            <li>
              Replying <code>STOP</code> to any message received from
              1-877-740-4425; or
            </li>
            <li>
              Disabling the SMS delivery channel in your CinchLo Notification
              Preferences page.
            </li>
          </ul>
          <p>
            Reply <code>HELP</code> to any message from 1-877-740-4425 for help
            instructions. Message and data rates may apply per your mobile
            carrier plan. See the full Terms & SMS Consent page for the complete
            consent disclosure.
          </p>
        </div>

        <div className='legal-section'>
          <h2>Retention</h2>
          <ul>
            <li>
              Authentication and activity logs: retained in accordance with FM
              Home Loans' records retention policy and applicable regulatory
              requirements (including 23 NYCRR Part 500 minimums)
            </li>
            <li>
              SMS consent records: retained while the user is active and for at
              least seven (7) years thereafter, as required for regulatory
              recordkeeping
            </li>
            <li>
              Notification delivery records: retained for at least twenty-four
              (24) months
            </li>
            <li>
              Mobile phone numbers: deleted upon opt-out unless retention is
              required for regulatory, audit, or legal-hold purposes
            </li>
          </ul>
        </div>

        <div className='legal-section'>
          <h2>Security</h2>
          <ul>
            <li>
              Data is encrypted in transit (TLS 1.2 or higher) and at rest
            </li>
            <li>
              Access to CinchLo requires authentication via Microsoft Entra ID
              single sign-on with multi-factor authentication
            </li>
            <li>
              Authorization within the application is inherited from each user's
              Encompass roles and access groups
            </li>
            <li>Administrative actions and access events are audit-logged</li>
            <li>
              CinchLo is operated under FM Home Loans' cybersecurity program
              established pursuant to 23 NYCRR Part 500
            </li>
          </ul>
        </div>

        <div className='legal-section'>
          <h2>Your Choices</h2>
          <p>
            CinchLo users are FM Home Loans personnel. With respect to
            information collected through CinchLo:
          </p>
          <ul>
            <li>
              Notification preferences: manage opt-ins, opt-outs, and quiet
              hours in your in-app Notification Preferences page
            </li>
            <li>
              Mobile number: update or remove in your Notification Preferences
              page; replying <code>STOP</code> to an SMS message also stops
              further SMS
            </li>
            <li>
              Access & correction requests: contact{' '}
              <a href='mailto:support@fmm.com'>support@fmm.com</a>
            </li>
          </ul>
        </div>

        <div className='legal-section'>
          <h2>Changes to This Policy</h2>
          <p>
            FM Home Loans may update this Privacy Policy from time to time.
            Material changes will be communicated through CinchLO and/or
            corporate email. The effective date at the top of this page
            indicates the most recent revision.
          </p>
        </div>

        <div className='legal-section'>
          <h2>Contact</h2>
          <div className='legal-contact'>
            <p>FM Home Loans, LLC</p>
            <p>2329 Nostrand Avenue</p>
            <p>Brooklyn, NY</p>
            <br />
            <p>
              Support & privacy inquiries:{' '}
              <a href='mailto:support@fmm.com'>support@fmm.com</a>
            </p>
            <p>Chief Information Security Officer: Asher Fhima</p>
            <p>
              General privacy policy (consumer/borrower):{' '}
              <a
                href='https://www.fmm.com/privacy'
                target='_blank'
                rel='noopener noreferrer'
              >
                www.fmm.com/privacy
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
